// =============================================================================
// permissions_store.js  — API-backed Permissions Store
//
// Provides a React context that:
//   1. Loads effective permissions from the API after authentication.
//   2. Exposes canView(module), canEdit(module), hasModule(module) helpers.
//   3. Provides role CRUD (backed by REST API).
//   4. Owner bypass: is_owner=true → all checks pass.
//
// Usage:
//   const { canView, canEdit, isOwner, roles, loading } = usePermissionsStore();
//   if (!canView('messaging.messages')) return null;
// =============================================================================

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import rolesService from "../../services/roles.service";

// ---------------------------------------------------------------------------
// Access-level constants (keep in sync with backend)
// ---------------------------------------------------------------------------
export const ACCESS = { NONE: "none", VIEW: "view", EDIT: "edit" };

const LEVEL_RANK = { none: 0, view: 1, edit: 2 };
const satisfies = (actual, required) =>
  (LEVEL_RANK[actual] ?? 0) >= (LEVEL_RANK[required] ?? 0);

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const PermissionsContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function PermissionsProvider({ children }) {
  const requestVersion = useRef(0);
  const rolesRequestVersion = useRef(0);
  // effective permissions loaded from API: { is_owner, modules: { module_key: level } }
  const [effectivePerms, setEffectivePerms] = useState({ is_owner: false, modules: {} });
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [permsLoading, setPermsLoading] = useState(false);

  // role list (from API)
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // ---------------------------------------------------------------------------
  // Load effective permissions (called after auth is confirmed)
  // ---------------------------------------------------------------------------
  const resetPermissions = useCallback(() => {
    requestVersion.current += 1;
    rolesRequestVersion.current += 1;
    setEffectivePerms({ is_owner: false, modules: {} });
    setPermsLoaded(false);
    setPermsLoading(false);
    setRoles([]);
    setRolesLoaded(false);
  }, []);

  const loadEffectivePermissions = useCallback(async () => {
    const version = ++requestVersion.current;
    setPermsLoading(true);
    try {
      const resp = await rolesService.getEffectivePermissions();
      if (version !== requestVersion.current) return;
      setEffectivePerms(resp.data || { is_owner: false, modules: {} });
      setPermsLoaded(true);
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("[permissions] failed to load effective permissions:", err);
      setEffectivePerms({ is_owner: false, modules: {} });
      setPermsLoaded(true);
    } finally {
      if (version === requestVersion.current) setPermsLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Load roles list
  // ---------------------------------------------------------------------------
  const loadRoles = useCallback(async () => {
    const version = ++rolesRequestVersion.current;
    try {
      const resp = await rolesService.listRoles();
      if (version !== rolesRequestVersion.current) return;
      setRoles(resp.data || []);
      setRolesLoaded(true);
    } catch (err) {
      if (version !== rolesRequestVersion.current) return;
      setRoles([]);
      console.error("[permissions] failed to load roles:", err);
      setRolesLoaded(true);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Authorization helpers
  // ---------------------------------------------------------------------------
  const isOwner = effectivePerms.is_owner === true;

  /** Returns the effective access level for a module */
  const getLevel = useCallback(
    (moduleKey) => {
      if (isOwner) return ACCESS.EDIT;
      return effectivePerms.modules?.[moduleKey] ?? ACCESS.NONE;
    },
    [isOwner, effectivePerms],
  );

  /** True if the current user has at least VIEW on the module */
  const canView = useCallback(
    (moduleKey) => {
      if (isOwner) return true;
      return satisfies(effectivePerms.modules?.[moduleKey] ?? "none", "view");
    },
    [isOwner, effectivePerms],
  );

  /** True if the current user has at least EDIT on the module */
  const canEdit = useCallback(
    (moduleKey) => {
      if (isOwner) return true;
      return satisfies(effectivePerms.modules?.[moduleKey] ?? "none", "edit");
    },
    [isOwner, effectivePerms],
  );

  /**
   * True if the module is accessible at all (view OR edit).
   * Used for menu filtering and route guards.
   */
  const hasModule = useCallback(
    (moduleKey) => canView(moduleKey),
    [canView],
  );

  /**
   * Check a specific level ('view' or 'edit').
   * Convenience for dynamic checks: hasPermission('messaging.inbox', 'edit')
   */
  const hasPermission = useCallback(
    (moduleKey, level = "view") => {
      if (isOwner) return true;
      return satisfies(effectivePerms.modules?.[moduleKey] ?? "none", level);
    },
    [isOwner, effectivePerms],
  );

  // ---------------------------------------------------------------------------
  // Role CRUD (API-backed)
  // ---------------------------------------------------------------------------

  const createRole = useCallback(async (payload) => {
    const resp = await rolesService.createRole({
      name: payload.name,
      description: payload.description || "",
    });
    const newRole = resp.data;
    setRoles((prev) => [...prev, newRole]);
    return newRole.id;
  }, []);

  const updateRole = useCallback(async (roleId, patch) => {
    const resp = await rolesService.updateRole(roleId, {
      name: patch.name,
      description: patch.description,
    });
    setRoles((prev) => prev.map((r) => (r.id === roleId ? resp.data : r)));
    return resp.data;
  }, []);

  const deleteRole = useCallback(async (roleId) => {
    await rolesService.deleteRole(roleId);
    setRoles((prev) => prev.filter((r) => r.id !== roleId));
  }, []);

  const setRolePermissions = useCallback(async (roleId, permissions) => {
    const resp = await rolesService.setPermissions(roleId, permissions);
    setRoles((prev) => prev.map((r) => (r.id === roleId ? resp.data : r)));
    return resp.data;
  }, []);

  // ---------------------------------------------------------------------------
  // Recipient role assignment
  // ---------------------------------------------------------------------------

  const assignRoleToRecipient = useCallback(
    (recipientId, roleId) => rolesService.assignRole(recipientId, roleId),
    [],
  );

  const removeRoleFromRecipient = useCallback(
    (recipientId, roleId) => rolesService.removeRole(recipientId, roleId),
    [],
  );

  const setRecipientRole = useCallback(
    (recipientId, roleId) => rolesService.setRecipientRole(recipientId, roleId),
    [],
  );

  // Refresh after role assignments change
  const refreshEffectivePermissions = loadEffectivePermissions;

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const value = useMemo(
    () => ({
      // Authorization
      isOwner,
      permsLoaded,
      permsLoading,
      effectivePerms,
      canView,
      canEdit,
      hasModule,
      hasPermission,
      getLevel,

      // Roles list
      roles,
      rolesLoaded,

      // Actions
      resetPermissions,
      loadEffectivePermissions,
      refreshEffectivePermissions,
      loadRoles,
      createRole,
      updateRole,
      deleteRole,
      setRolePermissions,
      assignRoleToRecipient,
      removeRoleFromRecipient,
      setRecipientRole,
    }),
    [
      isOwner,
      permsLoaded,
      permsLoading,
      effectivePerms,
      canView,
      canEdit,
      hasModule,
      hasPermission,
      getLevel,
      roles,
      rolesLoaded,
      resetPermissions,
      loadEffectivePermissions,
      refreshEffectivePermissions,
      loadRoles,
      createRole,
      updateRole,
      deleteRole,
      setRolePermissions,
      assignRoleToRecipient,
      removeRoleFromRecipient,
      setRecipientRole,
    ],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissionsStore() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error("usePermissionsStore must be used within a PermissionsProvider");
  }
  return ctx;
}

export default PermissionsContext;
